import { useRouter } from 'next/router';
import { forwardRef, ReactNode, useCallback, useMemo, useState } from 'react';
import { useTranslate } from 'react-t7e';
import { ConstraintContainer } from 'src/components/common/ConstraintContainer';
import { VerticalMenu } from 'src/components/common/VerticalMenu';
import { WorldRegion } from 'src/modules/experts/components/WorldMap';
import { InteractiveWorldMap } from 'src/modules/experts/components/WorldMap/InteractiveWorldMap';
import { useSpeakersRegionsQuery } from 'src/modules/experts/hooks/useSpeakersRegionsQuery';
import { route } from 'src/services/route';
import { media } from 'src/styles/designs';
import { redTheme } from 'src/styles/theme';
import styled from 'styled-components';
import { Media } from 'vendor/mods/media-breakpoints-fresnel/components/Media';
import { Theme } from 'vendor/mods/styled-components';

export interface LocalExpertsHeaderProps {
  children: ReactNode;
}

const Container = styled.header`
  position: relative;
  background: ${({ theme }) => theme.gradients.primary};
  color: ${({ theme }) => theme.text.palette.primary};
  ${media.w.greaterThanOrEqual.desktop} {
    min-height: 600px;
  }
`;

const ContentContainer = styled(ConstraintContainer)`
  display: flex;
  justify-content: space-between;
  padding-top: 20px;
  padding-bottom: 50px;
`;

const LocationContainer = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: column;
  gap: 20px;

  max-width: 320px;
`;

const Title = styled.h1`
  font-weight: 300;
  font-size: 32px;
  text-transform: uppercase;
`;

export const LocalExpertsHeader = forwardRef<
  HTMLDivElement,
  LocalExpertsHeaderProps
>(function LocalExpertsHeader({ children }, ref) {
  const { _ } = useTranslate();
  const router = useRouter();

  const { data: regions } = useSpeakersRegionsQuery();

  const activeCountry = useMemo(
    () =>
      regions
        ?.flatMap((r) => r.countries)
        .find((c) => c.slug === router.query.country)?.slug,
    [regions, router.query.country],
  );

  const [activeRegion, setActiveRegion] = useState<WorldRegion>();
  const [hoveredRegion, setHoveredRegion] = useState<WorldRegion>();
  const [hoveredCountry, setHoveredCountry] = useState<string>();

  const availableCountries = useMemo(
    () => regions?.flatMap((r) => r.countries.map((c) => c.slug)),
    [regions],
  );

  const options = useMemo(
    () =>
      regions?.map((region) => ({
        group: _('Regions'),
        label: region.name,
        value: region.slug as WorldRegion,
        subitems: region.countries.map((country) => ({
          label: country.name,
          value: country.slug,
        })),
      })) ?? [],
    [_, regions],
  );

  const onCountryChoose = useCallback(
    (slug: string) => {
      router.push(route.experts.local.country(slug), undefined, {
        shallow: true,
      });
    },
    [router],
  );

  const resetActiveOption = useCallback(() => {
    setActiveRegion(undefined);
    router.push(route.experts.local.page(), undefined, {
      shallow: true,
    });
  }, [router]);

  return (
    <Theme theme={redTheme}>
      <Container ref={ref}>
        {children}

        <ContentContainer>
          <LocationContainer>
            <Title>{_('experts.local.header')}</Title>

            <VerticalMenu
              activeOption={activeRegion}
              activeSubItem={activeCountry}
              onOptionClick={setActiveRegion}
              onOptionHover={setHoveredRegion}
              onSubItemClick={onCountryChoose}
              onSubItemHover={setHoveredCountry}
              resetActiveOption={resetActiveOption}
              options={options}
            />
          </LocationContainer>

          <Media greaterThanOrEqual="desktop">
            <InteractiveWorldMap
              availableCountries={availableCountries}
              activeCountries={[activeCountry, hoveredCountry]}
              activeRegions={activeRegion ? undefined : [hoveredRegion]}
              onCountryClick={(country) => {
                onCountryChoose(country.slug);
              }}
            />
          </Media>
        </ContentContainer>
      </Container>
    </Theme>
  );
});
